import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBlobUrl } from "@/hooks/use-assets";
import { api, formatApiError } from "@/lib/api";

export const CompanyLogo = ({ company, className = "h-8 w-8", placeholder = false }) => {
  const url = useBlobUrl(company?.logo_id ? `/business/companies/${company.id}/logo/${company.logo_id}` : null);
  if (!url) return placeholder ? <span className={`flex shrink-0 animate-pulse items-center justify-center rounded-lg bg-muted text-muted-foreground ${className}`}><Building2 className="h-1/2 w-1/2" /></span> : null;
  return <img src={url} alt={company.name} className={`shrink-0 rounded-lg bg-white object-contain ${className}`} data-testid="company-logo" />;
};

export const LogoUploader = ({ company }) => {
  const qc = useQueryClient();
  const { reload } = useAuth();
  const [busy, setBusy] = useState(false);
  const canEdit = company.my_role === "director";
  const done = async () => { qc.invalidateQueries(); await reload?.(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post(`/business/companies/${company.id}/logo`, fd); toast.success("Logo enregistré"); await done(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async () => { try { await api.delete(`/business/companies/${company.id}/logo`); toast.success("Logo retiré"); await done(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <section className="card-surface flex flex-wrap items-center gap-4 p-5" data-testid="biz-logo-card">
      {company.logo_id ? <CompanyLogo company={company} placeholder className="h-16 w-16 border border-border p-1" />
        : <span className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><Building2 className="h-6 w-6" /></span>}
      <div className="min-w-0 flex-1 basis-48"><h2 className="font-bold">Logo de l'entreprise</h2><p className="text-xs text-muted-foreground">PNG, JPEG ou WEBP, 10 Mo max. Affiché en haut de votre espace entreprise.</p></div>
      {canEdit ? (
        <div className="flex w-full gap-2 sm:w-auto">
          <Button asChild variant="outline" size="sm" className="rounded-full" disabled={busy}><label data-testid="biz-logo-upload">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}{company.logo_id ? "Changer" : "Ajouter"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onFile} disabled={busy} data-testid="biz-logo-input" /></label></Button>
          {company.logo_id && <Button variant="ghost" size="icon" onClick={remove} aria-label="Retirer le logo" data-testid="biz-logo-remove"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ) : <p className="text-xs text-muted-foreground">Modification réservée au dirigeant.</p>}
    </section>
  );
};
